import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { tribunesData, getAllThemes, formatTribuneDate } from '../data/tribunes';
import { 
  FileText, Download, Calendar, User, Tag, 
  ArrowRight, Quote, ChevronRight, ExternalLink,
  Share2, Twitter, Facebook, Linkedin, Check
} from 'lucide-react';

const TribunesPage = () => {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [expandedTribune, setExpandedTribune] = useState(null);
  const [copiedLink, setCopiedLink] = useState(null);

  const content = tribunesData[language] || tribunesData.fr;
  const tribunes = content.tribunes;
  const allThemes = getAllThemes(tribunes);

  // Filter tribunes by theme
  const filteredTribunes = selectedTheme 
    ? tribunes.filter(t => t.themes.includes(selectedTheme))
    : tribunes;

  // Share functions
  const copyLink = (tribune) => {
    const url = `${window.location.origin}/tribunes#${tribune.id}`;
    navigator.clipboard.writeText(url);
    setCopiedLink(tribune.id);
    setTimeout(() => setCopiedLink(null), 2000);
  };

  const shareTwitter = (tribune) => {
    const url = `${window.location.origin}/tribunes#${tribune.id}`;
    const text = `${tribune.title} - @ADA_CI`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
  };

  const shareFacebook = (tribune) => {
    const url = `${window.location.origin}/tribunes#${tribune.id}`;
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
  };

  const shareLinkedin = (tribune) => {
    const url = `${window.location.origin}/tribunes#${tribune.id}`;
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#1A0F2E]' : 'bg-[#FAFAFA]'}`}>
      {/* Hero Section */}
      <section className={`relative py-24 lg:py-32 overflow-hidden ${isDark ? 'bg-[#1A0F2E]' : 'bg-white'}`}>
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className={`absolute -top-40 -right-40 w-80 h-80 rounded-full ${isDark ? 'bg-[#5B2D8E]/20' : 'bg-[#5B2D8E]/5'} blur-3xl`} />
          <div className={`absolute -bottom-40 -left-40 w-80 h-80 rounded-full ${isDark ? 'bg-[#F7C948]/10' : 'bg-[#F7C948]/5'} blur-3xl`} />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-sm text-xs font-bold tracking-[0.2em] uppercase mb-6 ${
              isDark ? 'bg-[#F7C948]/20 text-[#F7C948]' : 'bg-[#5B2D8E]/10 text-[#5B2D8E]'
            }`}>
              <FileText className="w-4 h-4" />
              {content.pageTitle}
            </span>
            
            <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-6 ${isDark ? 'text-white' : 'text-[#0A0510]'}`}>
              {content.pageSubtitle}
            </h1>
            
            <p className={`text-lg leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {content.pageDescription}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter Section */}
      {allThemes.length > 0 && (
        <section className={`py-6 border-b ${isDark ? 'border-white/10 bg-[#150A20]' : 'border-gray-200 bg-white'}`}>
          <div className="container-custom">
            <div className="flex flex-wrap items-center gap-3">
              <span className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {content.filterByTheme}:
              </span>
              <button
                onClick={() => setSelectedTheme(null)}
                className={`px-4 py-2 rounded-sm text-sm font-medium transition-all ${
                  selectedTheme === null
                    ? 'bg-[#F7C948] text-[#0A0510]'
                    : isDark 
                      ? 'bg-white/5 text-white hover:bg-white/10' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {content.allTribunes}
              </button>
              {allThemes.map((theme) => (
                <button
                  key={theme}
                  onClick={() => setSelectedTheme(theme)}
                  className={`px-4 py-2 rounded-sm text-sm font-medium transition-all ${
                    selectedTheme === theme
                      ? 'bg-[#F7C948] text-[#0A0510]'
                      : isDark 
                        ? 'bg-white/5 text-white hover:bg-white/10' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {theme}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Tribunes List */}
      <section className="py-16 lg:py-24">
        <div className="container-custom">
          {filteredTribunes.length === 0 ? (
            <div className={`text-center py-16 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {content.noTribunes}
            </div>
          ) : (
            <div className="space-y-8">
              {filteredTribunes.map((tribune, index) => (
                <motion.article
                  key={tribune.id}
                  id={tribune.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`group relative rounded-sm border overflow-hidden transition-all duration-500 ${
                    isDark 
                      ? 'bg-[#150A20] border-white/5 hover:border-white/15' 
                      : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-xl'
                  }`}
                >
                  {/* Accent bar */}
                  <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-[#F7C948] to-[#5B2D8E]" />

                  <div className="p-8 lg:p-10 pl-10 lg:pl-12">
                    {/* Header */}
                    <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                      <div>
                        {/* Themes */}
                        <div className="flex flex-wrap gap-2 mb-4">
                          {tribune.themes.map((theme) => (
                            <span
                              key={theme}
                              className={`inline-flex items-center gap-1 px-3 py-1 rounded-sm text-xs font-medium ${
                                isDark ? 'bg-[#5B2D8E]/30 text-[#9B7FCE]' : 'bg-[#5B2D8E]/10 text-[#5B2D8E]'
                              }`}
                            >
                              <Tag className="w-3 h-3" />
                              {theme}
                            </span>
                          ))}
                        </div>

                        {/* Title */}
                        <h2 className={`text-2xl lg:text-3xl font-black tracking-tight mb-2 ${isDark ? 'text-white' : 'text-[#0A0510]'}`}>
                          {tribune.title}
                        </h2>
                        
                        {/* Subtitle */}
                        <p className={`text-lg ${isDark ? 'text-[#F7C948]' : 'text-[#5B2D8E]'} font-medium`}>
                          {tribune.subtitle}
                        </p>
                      </div>

                      {/* Download Button */}
                      <a
                        href={tribune.pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-5 py-3 rounded-sm bg-[#F7C948] text-[#0A0510] font-bold text-sm hover:bg-[#E5A700] transition-all hover:-translate-y-1"
                        data-testid={`download-tribune-${tribune.id}`}
                      >
                        <Download className="w-4 h-4" />
                        {content.downloadPdf}
                      </a>
                    </div>

                    {/* Meta */}
                    <div className={`flex flex-wrap items-center gap-4 text-sm mb-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      <span className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {content.publishedOn} {formatTribuneDate(tribune.date, language)}
                      </span>
                      <span className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        {content.by} {tribune.author}
                      </span>
                    </div>

                    {/* Summary */}
                    <p className={`text-base leading-relaxed mb-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      {tribune.summary}
                    </p>

                    {/* Key Points */}
                    {tribune.keyPoints && (
                      <div className={`mb-6 p-5 rounded-sm ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                        <h4 className={`text-sm font-bold uppercase tracking-wider mb-3 ${isDark ? 'text-white' : 'text-[#0A0510]'}`}>
                          Points clés
                        </h4>
                        <ul className="space-y-2">
                          {tribune.keyPoints.map((point, i) => (
                            <li key={i} className={`flex items-start gap-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                              <ChevronRight className="w-4 h-4 text-[#F7C948] mt-0.5 flex-shrink-0" />
                              {point}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Quote */}
                    {tribune.quote && (
                      <blockquote className={`relative pl-6 py-4 mb-6 border-l-4 border-[#F7C948] ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        <Quote className={`absolute -left-3 -top-2 w-6 h-6 ${isDark ? 'text-[#F7C948]/30' : 'text-[#F7C948]/50'}`} />
                        <p className="italic text-lg leading-relaxed">
                          "{tribune.quote}"
                        </p>
                      </blockquote>
                    )}

                    {/* Expandable Content */}
                    {tribune.content && (
                      <div className="mb-6">
                        <button
                          onClick={() => setExpandedTribune(expandedTribune === tribune.id ? null : tribune.id)}
                          className={`flex items-center gap-2 text-sm font-bold transition-all ${
                            isDark ? 'text-[#F7C948] hover:text-[#FFD966]' : 'text-[#5B2D8E] hover:text-[#3E1A66]'
                          }`}
                        >
                          {expandedTribune === tribune.id ? 'Réduire' : content.readMore}
                          <ArrowRight className={`w-4 h-4 transition-transform ${expandedTribune === tribune.id ? 'rotate-90' : ''}`} />
                        </button>
                        
                        {expandedTribune === tribune.id && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className={`mt-4 pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}
                          >
                            <div className={`prose prose-lg max-w-none ${isDark ? 'prose-invert' : ''}`}>
                              {tribune.content.split('\n\n').map((paragraph, i) => (
                                <p key={i} className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                  {paragraph}
                                </p>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </div>
                    )}

                    {/* Share Section */}
                    <div className={`flex flex-wrap items-center justify-between gap-4 pt-6 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                      <span className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {content.shareTitle}
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => copyLink(tribune)}
                          className={`p-2.5 rounded-sm transition-all ${
                            copiedLink === tribune.id
                              ? 'bg-green-500 text-white'
                              : isDark 
                                ? 'bg-white/5 text-white hover:bg-white/10' 
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          title="Copier le lien"
                        >
                          {copiedLink === tribune.id ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => shareTwitter(tribune)}
                          className={`p-2.5 rounded-sm transition-all ${
                            isDark 
                              ? 'bg-white/5 text-white hover:bg-[#1DA1F2] hover:text-white' 
                              : 'bg-gray-100 text-gray-700 hover:bg-[#1DA1F2] hover:text-white'
                          }`}
                          title="Partager sur Twitter"
                        >
                          <Twitter className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => shareFacebook(tribune)}
                          className={`p-2.5 rounded-sm transition-all ${
                            isDark 
                              ? 'bg-white/5 text-white hover:bg-[#1877F2] hover:text-white' 
                              : 'bg-gray-100 text-gray-700 hover:bg-[#1877F2] hover:text-white'
                          }`}
                          title="Partager sur Facebook"
                        >
                          <Facebook className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => shareLinkedin(tribune)}
                          className={`p-2.5 rounded-sm transition-all ${
                            isDark 
                              ? 'bg-white/5 text-white hover:bg-[#0A66C2] hover:text-white' 
                              : 'bg-gray-100 text-gray-700 hover:bg-[#0A66C2] hover:text-white'
                          }`}
                          title="Partager sur LinkedIn"
                        >
                          <Linkedin className="w-4 h-4" />
                        </button>
                        <a
                          href={tribune.pdfUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`p-2.5 rounded-sm transition-all ${
                            isDark 
                              ? 'bg-white/5 text-white hover:bg-[#F7C948] hover:text-[#0A0510]' 
                              : 'bg-gray-100 text-gray-700 hover:bg-[#F7C948] hover:text-[#0A0510]'
                          }`}
                          title="Ouvrir le PDF"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className={`py-16 ${isDark ? 'bg-[#150A20]' : 'bg-[#5B2D8E]'}`}>
        <div className="container-custom text-center">
          <h2 className="text-3xl lg:text-4xl font-black text-white mb-4">
            {language === 'fr' ? 'Restez informé' : language === 'pt' ? 'Fique informado' : 'Stay informed'}
          </h2>
          <p className="text-white/70 mb-8 max-w-xl mx-auto">
            {language === 'fr' 
              ? 'Recevez nos prochaines tribunes et prises de position directement dans votre boîte mail.'
              : language === 'pt'
              ? 'Receba as nossas próximas tribunas e posições diretamente na sua caixa de correio.'
              : 'Receive our next op-eds and positions directly in your inbox.'}
          </p>
          <a
            href="/rejoindre"
            className="inline-flex items-center gap-3 px-8 py-4 bg-[#F7C948] text-[#0A0510] font-bold rounded-sm hover:bg-[#FFD966] transition-all hover:-translate-y-1"
          >
            {language === 'fr' ? 'S\'inscrire' : language === 'pt' ? 'Inscrever-se' : 'Subscribe'}
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>
    </div>
  );
};

export default TribunesPage;
